'use client';

import Button from '@/components/ui/Button';
import Dialog from '@/components/ui/Dialog';
import { cn } from '@/lib/utils/utils';
import { useState, useEffect } from 'react';
import { Tab } from '@headlessui/react';
import { Copy } from 'lucide-react';
import { fetchUserTransaction, useCreateUserInvoice } from '@/features/user';
import { InvoiceResponse } from '@/shared/user/transaction';
import Image from 'next/image';
import Link from 'next/link';
import { toast } from 'react-toastify';
import { AuthTokenStorageService } from '@/lib/auth';

const rechargeOptions = [
  { value: 10000, label: '10,000 төгрөг' },
  { value: 20000, label: '20,000 төгрөг' },
  { value: 50000, label: '50,000 төгрөг' },
  { value: 100000, label: '100,000 төгрөг' },
  { value: 200000, label: '200,000 төгрөг' },
  { value: 300000, label: '300,000 төгрөг' },
  { value: 400000, label: '400,000 төгрөг' },
  { value: 500000, label: '500,000 төгрөг' },
];

interface RechargeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onRecharge: () => void | Promise<void>;
  autoMode?: boolean; // Auto mode: skip amount selection, auto amount and create invoice automatically
  autoAmount?: number;
}

export default function RechargeDialog({
  isOpen,
  onClose,
  onRecharge,
  autoMode = false,
  autoAmount = 5000,
}: RechargeDialogProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [step, setStep] = useState<number>(1);
  const {
    mutateAsync: createUserInvoiceMutation,
    isPending: isCreatingInvoice,
  } = useCreateUserInvoice();
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const isMobile = AuthTokenStorageService.checkIsMobile();

  const createInvoiceAuto = async () => {
    try {
      const invoiceData = await createUserInvoiceMutation({
        amount: autoAmount,
        useZangiaInvoiceCode: true,
        forWhat: 'zangia_plus',
      });
      setInvoice(invoiceData);
      setStep(2);
    } catch {
      // Error toast is handled by useCreateUserInvoice
    }
  };

  // Auto mode: automatically create invoice when dialog opens
  useEffect(() => {
    if (isOpen && autoMode && !invoice && !isCreatingInvoice) {
      setSelectedAmount(autoAmount);
      createInvoiceAuto();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, autoMode]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedAmount(null);
      setStep(autoMode ? 2 : 1);
      setInvoice(null);
    } else if (autoMode) {
      // In auto mode, start at step 2
      setStep(2);
    }
  }, [isOpen, autoMode]);

  const handleRecharge = async () => {
    if (invoice) {
      setTransactionLoading(true);
      try {
        const transaction = await fetchUserTransaction(invoice.id);
        if (transaction.status === 1) {
          await Promise.resolve(onRecharge());
          onClose();
        } else {
          toast.warning(
            'Таны төлбөр төлөгдөөгүй байгаа тул төлбөрөө төлнө үү.'
          );
        }
      } catch {
      } finally {
        setTransactionLoading(false);
      }
    }
  };

  const createInvoice = async () => {
    if (selectedAmount === null) return;

    try {
      const invoiceData = await createUserInvoiceMutation({
        amount: selectedAmount,
      });
      setInvoice(invoiceData);
      setStep(2);
    } catch {
      // Error toast is handled by useCreateUserInvoice
    }
  };

  return (
    <Dialog
      closeOnClickOutside={false}
      isOpen={isOpen}
      onClose={onClose}
      title={<p>Данс цэнэглэх</p>}
    >
      {step === 1 && !autoMode && (
        <div>
          <div className="grid grid-cols-2 gap-3">
            {rechargeOptions.map((option) => (
              <Button
                key={option.value}
                type="button"
                variant="outline"
                onClick={() => setSelectedAmount(option.value)}
                className={cn(
                  'h-auto w-full rounded-lg p-3',
                  selectedAmount === option.value
                    ? 'border-primary bg-primary-50 text-primary'
                    : 'border-gray-200 text-gray-800'
                )}
              >
                {option.label}
              </Button>
            ))}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              type="button"
              variant="primary"
              onClick={() => void createInvoice()}
              disabled={!selectedAmount}
              loading={isCreatingInvoice}
              className="h-10 px-5"
            >
              Үргэлжлүүлэх
            </Button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="mt-4">
          <Tab.Group>
            <Tab.List className="flex border-b border-gray-200">
              <Tab className="py-2 px-4 text-sm font-medium outline-none border-b-2 border-primary text-primary">
                QPAY
              </Tab>
            </Tab.List>
            <Tab.Panels className="mt-4">
              <Tab.Panel>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-center mb-4">
                    <div className="w-48 h-48 mx-auto bg-white border border-gray-300 items-center justify-center hidden md:flex">
                      {invoice?.qr_image ? (
                        <Image
                          src={`data:image/png;base64,${invoice.qr_image}`}
                          alt="QR code"
                          width={196}
                          height={196}
                        />
                      ) : (
                        <div
                          className="h-full w-full animate-pulse rounded bg-gray-200"
                          aria-label="QR код ачааллаж байна"
                          role="status"
                        />
                      )}
                    </div> 
                    <div className="grid grid-cols-4 gap-3 md:hidden">
                      {invoice?.urls.map((bank) => {
                        const content = (
                          <div className="bg-white p-3 rounded-xl shadow-sm flex items-center justify-center cursor-pointer">
                            <Image
                              src={bank.logo ?? ''}
                              alt={bank.name ?? 'Bank'}
                              className="w-8 h-8"
                              width={32}
                              height={32}
                            />
                          </div>
                        );

                        return !isMobile ? (
                          <Link href={bank.link ?? ''} key={bank.name ?? ''}>
                            {content}
                          </Link>
                        ) : (
                          <div
                            key={bank.name}
                            onClick={() => {
                              window.ReactNativeWebView?.postMessage(
                                JSON.stringify({ type: 'openBank', data: bank })
                              );
                            }}
                          >
                            {content}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">
                    Сонгосон дүн:{' '}
                    <span className="font-bold">
                      {autoMode
                        ? autoAmount?.toLocaleString()
                        : selectedAmount?.toLocaleString()}
                      ₮
                    </span>
                  </p>
                  <p className="text-sm text-gray-600">
                    QR кодыг уншуулж төлбөрөө төлнө үү. Төлбөр амжилттай
                    хийгдсэний дараа таны данс автоматаар цэнэглэгдэх болно.
                  </p>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>

          <div className="mt-4 flex justify-end gap-2">
            {!autoMode && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="h-10 px-5"
              >
                Буцах
              </Button>
            )}
            <Button
              type="button"
              variant="primary"
              onClick={() => void handleRecharge()}
              loading={transactionLoading}
              className="h-10 px-5"
            >
              Баталгаажуулах
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
